import Model from "../common/model";

/**
 * @model CreditsManagersModel
 * @description
 * Represents the subscription and credit management data structure for a business entity within the AquaCloud application.
 * This model serves as the central ledger for tracking a user's or business's entitlement to platform features,
 * specifically focusing on "Credits" and "Priority Postings" which may be consumed during trading or listing activities.
 *
 * **Purpose:**
 * The primary purpose of this model is to enforce business logic related to monetization and usage limits.
 * It answers questions such as:
 * - "Does this user have enough credits to post a new listing?"
 * - "Is the user's subscription currently active?"
 * - "What devices (IoT hardware) are licensed under this account?"
 *
 * **Key Responsibilities:**
 * 1. **Balance Tracking**: Monitors `creditsIssued` vs `creditsUsed` and `priorityPostingsIssued` vs `priorityPostingsUsed`.
 * 2. **Subscription Lifecycle**: Defines the validity period (`subscriptionStartDate` to `subscriptionEndDate`) and expiration status (`isExpired`).
 * 3. **Policy Association**: Links the record to a specific `creditPolicyID` which defines the rules of the subscription plan.
 * 4. **Device Licensing**: Aggregates details about associated hardware (`devicesDetails`), including payment status and license validity for devices like BluePod and BluEye.
 *
 * **Data Flow:**
 * - **Creation**: Generated when a business purchases a subscription plan.
 * - **Updates**: Modified when credits are consumed (e.g., posting a lead) or when a subscription is renewed/upgraded.
 * - **Retrieval**: Fetched by `CreditsManagersService` to display the "My Plan" or "Wallet" section of the user profile.
 *
 * **User Roles & Permissions:**
 * - **System Admin**: Can issue manual credits or adjust policies.
 * - **Subscriber (Business Admin)**: Read-only access to view their balance and history.
 *
 * **Features:**
 * - Supports complex nested structures for device payments and tax calculations (GST).
 * - Tracks terms acceptance (`termsAccepted`).
 * - Handles both software credits and hardware licenses in a single view.
 *
 * @implements Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class CreditsManagersModel implements Model {

  /**
   * @type {string}
   * @description Unique identifier for the credit management record.
   */
  id: string;

  /**
   * @type {Date}
   * @description Timestamp when the record was created.
   */
  createdAt: Date;

  /**
   * @type {Date}
   * @description Timestamp when the record was last updated.
   */
  updatedAt: Date;

  /**
   * @type {string}
   * @description Total number of general credits issued to the account.
   * Stored as string to handle potential large numbers or formatting.
   */
  creditsIssued: string;

  /**
   * @type {string}
   * @description Total number of general credits consumed by the account.
   */
  creditsUsed: string;

  /**
   * @type {string}
   * @description Total number of priority/featured postings allowed.
   */
  priorityPostingsIssued: string;

  /**
   * @type {string}
   * @description Total number of priority/featured postings utilized.
   */
  priorityPostingsUsed: string;

  /**
   * @type {boolean}
   * @description Flag indicating if the subscription has expired.
   * True if current date > subscriptionEndDate.
   */
  isExpired: boolean;

  /**
   * @type {string}
   * @description ID of the person (user) associated with this credit account.
   */
  personID: string;

  /**
   * @type {any}
   * @description ID or object of the business entity owning these credits.
   */
  businessID: any;

  /**
   * @type {string}
   * @description Foreign key linking to the specific Credit Policy definition.
   */
  creditPolicyID: string;

  /**
   * @type {string}
   * @description ISO date string representing the start of the subscription validity.
   */
  subscriptionStartDate: string;

  /**
   * @type {string}
   * @description ISO date string representing the end of the subscription validity.
   */
  subscriptionEndDate: string;

  /**
   * @type {string}
   * @description ID of the user/system that created this record.
   */
  createdBy: string;

  /**
   * @type {string}
   * @description ID of the user/system that last updated this record.
   */
  updatedBy: string;

  /**
   * @type {boolean}
   * @description Flag indicating if the record has been updated since creation.
   */
  updated: boolean;

  /**
   * @type {string}
   * @description Display name of the associated business.
   */
  businessName: string;

  /**
   * @type {string}
   * @description Display name of the associated person.
   */
  personName: string;

  /**
   * @type {number}
   * @description Flag indicating if the entity is a "Blue Hatchery" (BH).
   * 1 = Yes, 0 = No.
   */
  isBH: number;

  /**
   * @type {boolean}
   * @description Indicates if the user has accepted the terms and conditions for this plan.
   */
  termsAccepted: boolean;

  /**
   * @type {SubscriberSubscriptionDuration}
   * @description Object detailing the duration of the subscription (e.g., "1 Year").
   */
  durationLeft: SubscriberSubscriptionDuration;

  /**
   * @type {SubscriberPolicyDetails}
   * @description Snapshot of the policy details applied to this subscription.
   */
  policyDetails: SubscriberPolicyDetails;

  /**
   * @type {SubscriberPaymentDetails}
   * @description Information regarding the payment made for this subscription.
   */
  paymentDetails: SubscriberPaymentDetails;

  /**
   * @type {Array<DeviceDetails>}
   * @description List of IoT devices and their specific license/payment details associated with this account.
   * @optional
   */
  devicesDetails?: Array<DeviceDetails>;

  /**
   * @constructor
   * @description Initializes a new instance of the CreditsManagersModel.
   */
  constructor() {
  }
}

/**
 * @model businessDetails
 * @description Helper class to structure business type information within the credit context.
 */
export class businessDetails {
  /** @type {string} @description Unique identifier. */
  id: string;
  /** @type {Array<businessType>} @description List of business types associated. */
  businessType: Array<businessType>
  constructor(init?: Partial<businessDetails>) {
    if (init) Object.assign(this, init);
  }
}

/**
 * @model businessType
 * @description Represents a specific classification of a business.
 */
export class businessType {
  /** @type {string} @description Internal MongoDB ID. */
  _id: string;
  /** @type {string} @description Business Type ID. */
  businessTypeID: string;
  /** @type {string} @description Human-readable name of the business type. */
  name: string
}

/**
 * @model SubscriberSubscriptionDuration
 * @description Defines the time span of a subscription.
 */
export class SubscriberSubscriptionDuration {
  /** @type {string} @description The numeric value of the duration (e.g., "12"). */
  period: string;
  /** @type {string} @description The unit of time (e.g., "Months", "Years"). */
  unit: string
}

/**
 * @model SubscriberPolicyDetails
 * @description Details about the specific policy rules applied.
 */
export class SubscriberPolicyDetails {
  /** @type {string} @description Policy ID. */
  id: string;
  /** @type {string} @description Policy Name. */
  name: string;
  /** @type {string} @description Internal flag or code for the policy. */
  policyFlag: string;
}

/**
 * @model SubscriberPaymentDetails
 * @description Comprehensive payment information for audit and receipt generation.
 */
export class SubscriberPaymentDetails {
  /** @type {any} @description Total amount paid. */
  amount: any;
  /** @type {string} @description Currency code (e.g., "INR", "USD"). */
  currency: string;
  /** @type {string} @description Date when payment was processed. */
  paidDate: string;
  /** @type {string} @description User ID or Name who made the payment. */
  paidBy: string;
  /** @type {string} @description Description of what the payment was for. */
  paidFor: string;
  /** @type {string} @description Method of payment (e.g., "Credit Card", "Net Banking"). */
  paymentMode: string;
  /** @type {string} @description Transaction reference ID from the payment gateway. */
  referenceID: string;
  /** @type {number} @description GST percentage applied. @optional */
  gstPercent?: number;
  /** @type {number} @description Calculated GST amount. @optional */
  gstCost?: number;
}

/**
 * @model DeviceDetails
 * @description
 * Detailed configuration and payment status for a specific IoT device linked to the credit account.
 * Handles complex nested structures for different device types (BluePod, BluEye).
 */
export class DeviceDetails {
  /** @type {string} @description Unique identifier for the device record. @optional */
  _id?: string;
  /** @type {string} @description ID referencing the device configuration. */
  deviceConfiguationID: string;
  /** @type {string} @description Serial number or unique hardware ID. */
	deviceNumber: string;
  /** @type {number} @description ID of the production unit associated with the device. */
  prodUnitId: number;
  /** @type {string} @description Name of the production unit. */
  prodUnitName: string;

  /**
   * @type {Object}
   * @description Configuration and cost details for BluePod devices.
   */
  bluePod: {
    /** @type {string} @description BluePod ID. */
    id: string;
    /** @type {string} @description BluePod Name. */
    name: string;
    /** @type {number} @description Base cost. */
    cost: number;
    /** @type {number} @description Tax percentage. */
    gstPercent: number;
    /** @type {number} @description Tax amount. */
    gstCost: number;
  };

  /**
   * @type {Object}
   * @description Configuration and licensing details for BluEye devices.
   */
  bluEye: {
    /** @type {number} @description Base cost. */
    cost: number;
    /** @type {number} @description Tax percentage. */
    gstPercent: number;
    /** @type {number} @description Tax amount. */
    gstCost: number;
    /** @type {string} @description License start date. */
    startDate: string;
    /** @type {string} @description License end date. */
    endDate: string;
    /** @type {string} @description Validity period description. */
    validity: string;
    /** @type {string} @description Remaining duration string. */
    durationLeft: string;
    /**
     * @type {Array}
     * @description Breakdown of user licenses purchased for this device.
     */
    userLicensesDetails: [
      {
        /** @type {string} @description Role ID for the license. */
        roleID: string;
        /** @type {string} @description Role Name (e.g., "Viewer", "Admin"). */
        roleName: string;
        /** @type {number} @description Count of licenses. */
        noOfLicenses: number;
        /** @type {number} @description Cost per license or total. */
        cost: number;
        /** @type {number} @description Tax percentage. */
        gstPercent: number;
        /** @type {number} @description Tax amount. */
        gstCost: number;
        /** @type {boolean} @description Is this a default license included with device. */
        isDefault: boolean
      }
    ]
  };

  /**
   * @type {Array}
   * @description History of payments made specifically for this device.
   */
  paymentDetails: [
    {
      /** @type {number} @description Amount paid. */
      cost: number;
      /** @type {string} @description Currency code. */
      currency: string;
      /** @type {string} @description Date of payment. */
      paidDate: string;
      /** @type {string} @description Purpose of payment. */
      paidFor: string;
      /** @type {string} @description Payer ID/Name. */
      paidBy: string;
      /** @type {string} @description Payment method. */
      paymentMode: string;
      /** @type {string} @description Transaction reference. */
      referenceID: string;
      /** @type {number} @description Tax percentage. */
      gstPercent: number;
      /** @type {number} @description Tax amount. */
      gstCost: number;
      /** @type {boolean} @description Flag for new device purchase. */
      isNewDevicePayment: boolean;
      /** @type {boolean} @description Flag for renewal payment. */
      isBluEyeRenewalPayment: boolean;
      /** @type {boolean} @description Flag for additional hardware payment. */
      isAdditionalBluPodPayment: boolean;
      /** @type {boolean} @description Flag for additional license payment. */
      isAdditionalUserLicensePayment: boolean;
    }
  ];

  /**
   * @type {boolean}
   * @description Indicates if this is an add-on device rather than a primary one.
   */
  isAdditionalDevice: boolean;

  /**
   * @type {number}
   * @description Discount percentage applied to this device.
   */
  additionalDiscountPercent: number;

  /**
   * @type {number}
   * @description Discount amount applied.
   */
  additionalDiscountCost: number;

  /**
   * @type {boolean}
   * @description Status of the device subscription.
   */
  isActive: boolean;

  /**
   * @type {boolean}
   * @description Terms acceptance flag for this specific device.
   */
  termsAccepted: boolean;

  /**
   * @type {number}
   * @description Total cost calculated for this device entry.
   * @optional
   */
  totalCost?: number
}
